import { parse as queryParse } from 'querystring';

import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Wizard } from 'react-use-wizard';
import { useForm } from 'react-hook-form';
import {
  addDays,
  startOfDay,
  format,
  getHours,
  parse,
  getMinutes,
  setMinutes,
  differenceInMinutes,
  isSameDay,
  isToday,
} from 'date-fns';
import { GetServerSideProps, GetStaticPropsResult } from 'next';
import router, { useRouter } from 'next/router';
import Head from 'next/head';
import { toast } from 'react-toastify';
import { flatten, maxBy, reduce } from 'ramda';
import CryptoJS from 'crypto-js';

import SEO from '@app/components/seo';
import Layout from '@app/layouts/page-default';
import WizardHeader from '@app/components/wizard/wizard-header';
import WizardFooter from '@app/components/wizard/wizard-footer';
import ButtonBar from '@app/components/button-bar';
import OtpInput from '@app/components/otp-input';
import { Movie, MovieSingle, MovieStatus } from '@app/types/movie';
import { firebase, functions } from '@app/config/fire-conf';
import { ButtonBarItem } from '@app/components/button-bar/button-bar';
import {
  Schedule,
  ScheduleSingle,
  Cinema,
  CinemaSingle,
  BookedSeat,
  SettingsBookingType,
} from '@app/types';
import MovieTickets from '@app/components/movie-tickets';
import {
  MovieTicketsHandle,
  WizardStepMovieTicketsProps,
} from '@app/components/movie-tickets/movie-tickets';
import Loading from '@app/components/loading';
import { initiateCheckout } from '@app/lib/facebook-pixel';
import { getDayOfWeek } from '@app/utils';
import WizardSummary from '@app/components/wizard/wizard-summary';
import { useRemoteConfig } from '@app/context/remote-config';
import Timeout from '@app/components/timeout';
import { useGoogleRecaptcha } from '@app/hooks';
import TermsPopup from '@app/components/popup';

export const ContentBlockStyled = styled.div`
  .content-container {
    ${tw`flex w-full flex-col justify-between mx-auto overflow-hidden`}
    max-width: 600px;

    h2 {
      ${tw`text-white text-3xl font-bold mb-6 my-6 mx-8 mt-24`}
      font-size: 32px;
    }
  }
`;

export const BuyTicketsStyled = styled.div`
  ${tw`flex flex-col mx-auto z-10 justify-between py-5 relative mx-5 lg:mx-auto`}
  max-width: 1360px;
`;

export const StepStyled = styled.div`
  ${tw`flex flex-col mx-auto`}

  max-width: 980px;
`;

export type WizardStep1FormPropTypes = {
  date: string;
  movie: string;
  cinema: string;
  showtime: string;
  scheduleObj?: Schedule;
};

export type WizardStep2FormPropTypes = {
  customer: {
    name: string;
    phoneNo: string;
    email: string;
  };
};

export type WizardSeats = {
  seats: BookedSeat[];
  temporaryBookingId?: string;
};

type Day = {
  value: string;
  label: string;
};

type BuyTicketsProps = {
  moviesStrObjs: string[];
  schedulesStrObjs: string[];
  cinemasStrObjs: string[];
  bookingSettingsStr: string;
};

const timeFormat = 'h:mm a';
const dateFormat = 'dd-MM-yyyy';

export default function BuyTickets({
  moviesStrObjs,
  schedulesStrObjs,
  cinemasStrObjs,
  bookingSettingsStr,
}: BuyTicketsProps): JSX.Element {
  const {
    register: registerStep1,
    handleSubmit: handleSubmitStep1,
    setValue: setValueStep1,
    getValues: getValuesStep1,
    clearErrors: clearErrorsStep1,
    trigger: triggerStep1,
    formState: { errors: errorsStep1 },
  } = useForm<WizardStep1FormPropTypes>({ mode: 'onChange' });
  const {
    register: registerStep2,
    handleSubmit: handleSubmitStep2,
    trigger: triggerStep2,
    getValues: getValuesStep2,
    formState: { errors: errorsStep2 },
  } = useForm<WizardStep2FormPropTypes>({
    mode: 'onChange',
    defaultValues: {
      customer: {
        phoneNo: '+94',
      },
    },
  });
  const {
    trigger: triggerStep3,
    handleSubmit: handleSubmitStep3,
    ...restUseFormReturn
  } = useForm<WizardStepMovieTicketsProps>({
    defaultValues: {
      adults: '0',
      children: '0',
      adultTicketPrice: 0,
      childTicketPrice: 0,
      childTicketPriceTotal: 0,
      adultTicketPriceTotal: 0,
      ticketsCount: 0,
      adultConvenienceFee: 0,
      childConvenienceFee: 0,
    },
  });
  const { getRecaptchaToken, loadRecaptchaScript } = useGoogleRecaptcha();
  const [movies, setMovies] = useState<Movie[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [cinemas, setCinemas] = useState<Cinema[]>([]);
  const [noSchedules, setNoSchedules] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpVerifiedPhone, setOtpVerifiedPhone] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpResendCountdown, setOtpResendCountdown] = useState(0);
  const [enablePay, setEnablePay] = useState(false);
  const [paymentType, setPaymentType] = useState('commercial');
  const [disablePay, setDisablePay] = useState(false);
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(true);
  const [currentSchedules, setCurrentSchedules] = useState<Schedule[]>([]);
  const [currentMovies, setCurrentMovies] = useState<Movie[]>([]);
  const [currentCinema, setCurrentCinema] = useState<CinemaSingle[]>([]);
  const [currentShowtime, setCurrentShowtime] = useState<Schedule[]>([]);
  const bookingSettings = useRef<SettingsBookingType>(JSON.parse(bookingSettingsStr));
  const [scheduleId, setScheduleId] = useState('');
  const [onlineFee, setOnlineFee] = useState(0);
  const [formData, setFormData] = useState<
    Partial<
      WizardStep1FormPropTypes &
        WizardStep2FormPropTypes &
        WizardSeats &
        WizardStepMovieTicketsProps & {
          newsletterSubscribe: boolean;
        }
    >
  >({ newsletterSubscribe: true });
  const [isValidStep, setIsValidStep] = useState<{
    step0: boolean;
    step1: boolean;
    step2: boolean;
  }>({ step0: false, step1: true, step2: true });
  const next28Days = useRef<Day[]>([]);
  const [noSchedulesPerDate, setNoSchedulesPerDate] = useState(false);
  const { asPath, query: routerQuery, push, reload } = useRouter();
  const query = queryParse(asPath.substr(asPath.indexOf('?') + 1));
  const firstTime = useRef(true);
  const dataReady = useRef(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const movieTicketRef = useRef<MovieTicketsHandle>(null);
  const { settings } = useRemoteConfig();
  const formRef = useRef<HTMLFormElement>(null);
  const [signature, setSignature] = useState<string>('');
  const datetime = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
  const transactionUUID = `uuid-${Math.random().toString(36).substring(2)}`;
  const referenceNumber = `ref-${Math.random().toString(36).substring(2)}`;
  const [isPopupAgree, setIsPopupAgree] = useState(false);

  const [params, setParams] = useState<{
    access_key: string;
    profile_id: string;
    transaction_uuid: string;
    signed_field_names: string;
    unsigned_field_names: string;
    signed_date_time: string;
    locale: string;
    transaction_type: string;
    reference_number: string;
    amount: string;
    currency: string;
    bill_to_forename: string;
    bill_to_surname: string;
    bill_to_email: string;
    bill_to_address_line1: string;
    bill_to_address_city: string;
    bill_to_address_postal_code: string;
    bill_to_address_state: string;
    bill_to_address_country: string;
  }>({
    access_key: process.env.NEXT_PUBLIC_CYBERSOURCE_ACCESS_KEY || '',
    profile_id: process.env.NEXT_PUBLIC_CYBERSOURCE_PROFILE_ID || '',
    transaction_uuid: transactionUUID,
    signed_field_names:
      'access_key,profile_id,transaction_uuid,signed_field_names,unsigned_field_names,signed_date_time,locale,transaction_type,reference_number,amount,currency,bill_to_forename,bill_to_surname,bill_to_email,bill_to_address_line1,bill_to_address_city,bill_to_address_postal_code,bill_to_address_state,bill_to_address_country',
    unsigned_field_names: '',
    signed_date_time: datetime,
    locale: 'en',
    transaction_type: 'sale',
    reference_number: referenceNumber,
    amount: '',
    currency: 'LKR',
    bill_to_forename: '',
    bill_to_surname: '',
    bill_to_email: '',
    bill_to_address_line1: '5 Sri Dalada Veediya',
    bill_to_address_city: 'Kandy',
    bill_to_address_postal_code: '20000',
    bill_to_address_state: 'Central Province',
    bill_to_address_country: 'SL',
  });

  const onSubmitStep1 = (data: WizardStep1FormPropTypes) => {
    const schedule = currentShowtime.find((s) => s.id === data.showtime);
    setFormData((currentData) => ({
      ...currentData,
      ...data,
      scheduleObj: schedule,
    }));
  };
  const onSubmitStep2 = (data: WizardStep2FormPropTypes) => {
    setFormData((currentData) => ({ ...currentData, ...data }));
  };
  const onSubmitStep3 = (values: WizardStepMovieTicketsProps): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      if (!scheduleId) {
        reject(new Error('Please select a showtime to continue'));
        return;
      }
      const totalTickets =
        parseInt(values.adults || '0', 10) + parseInt(values.children || '0', 10);
      if (values.ticketsCount === totalTickets) {
        // proceed
        if (values.ticketsCount === 0) {
          reject(new Error('Please select at least 1 seat to continue'));
        } else {
          try {
            setSectionLoading(true);
            // functions().useEmulator('localhost', 5001);
            getRecaptchaToken().then(async (recaptchaToken) => {
              const ipRes = await fetch('/api/client-ip');
              const { ip } = await ipRes.json();
              const temporaryBookSeats = functions().httpsCallable('temporaryBookSeatsV2');
              temporaryBookSeats({
                seatIds: JSON.parse(values.currentSelectionIds),
                scheduleId,
                recaptchaToken,
                user: {
                  email: formData.customer?.email,
                  name: formData.customer?.name,
                  mobileNumber: formData.customer?.phoneNo,
                  ip,
                  type: 'online',
                },
              })
                .then((response) => {
                  setSectionLoading(false);
                  if (response.data.type === 'error') {
                    if (movieTicketRef.current) {
                      movieTicketRef.current.reset();
                    }
                    reject(new Error(response.data.message));
                  } else {
                    // success
                    const bookedSeats = response.data.data as BookedSeat[];
                    setFormData((currentData) => ({
                      ...currentData,
                      ...restUseFormReturn.getValues(),
                    }));
                    setFormData((currentData) => ({
                      ...currentData,
                      seats: bookedSeats,
                      temporaryBookingId: response.data.tempBookingDocId,
                    }));
                    setParams((currentData) => ({
                      ...currentData,
                      amount: values.amountPaid ? String(values.amountPaid) : '0',
                      bill_to_forename: formData.customer?.name.split(' ')[0]
                        ? formData.customer?.name.split(' ')[0]
                        : 'Not Available',
                      bill_to_surname: formData.customer?.name.split(' ')[-1]
                        ? formData.customer?.name.split(' ')[-1]
                        : 'Not Available',
                      bill_to_email: formData.customer?.email || 'kccmultiplex@kandycitycentre.lk',
                    }));
                    initiateCheckout({
                      contents: {
                        id: formData.movie || '',
                        quantity: formData.ticketsCount || 0,
                      },
                      content_name: `${formData.movie} - ${formData.scheduleObj?.id}` || '',
                      currency: 'LKR',
                      num_items: formData.ticketsCount || 0,
                      value: formData.amountPaid || 0,
                    });
                    resolve(true);
                  }
                })
                .catch((error) => {
                  setSectionLoading(false);
                  reject(new Error(error instanceof Error ? error.message : String(error)));
                });
            });
          } catch (error) {
            setSectionLoading(false);
            reject(new Error(error instanceof Error ? error.message : String(error)));
          }
        }
      } else if (values.ticketsCount < totalTickets) {
        if (totalTickets > 8) {
          reject(new Error(`Maximum up to 8 tickets`));
        } else {
          reject(
            new Error(
              `Please select ${totalTickets - values.ticketsCount} more seat(s) to continue`,
            ),
          );
        }
      } else if (values.ticketsCount > totalTickets) {
        reject(
          new Error(
            `Please add ${values.ticketsCount - totalTickets} more attendee type(s) to continue`,
          ),
        );
      }
    });
  };

  const otpEnabled = settings?.otpEnabled ?? true;
  const otpResendTimeout = settings?.otpResendTimeout ?? 60; // seconds between resends
  const maxWindowAttempts = settings?.maxWindowAttempts ?? 3; // MAX_ATTEMPTS per window
  const maxTotalAttempts = settings?.maxTotalAttempts ?? 5; // MAX_LIFETIME_ATTEMPTS
  const maxOtpAttempts = settings?.maxOtpAttempts ?? 5; // MAX_OTP_ATTEMPTS (sends per verify)
  const handleSendOtp = async () => {
    if (otpResendCountdown > 0) {
      return;
    }
    const isPhoneValid = await triggerStep2('customer.phoneNo');
    if (!isPhoneValid) {
      return;
    }
    const phone = getValuesStep2('customer.phoneNo');
    const email = getValuesStep2('customer.email');
    try {
      const response = await fetch('/api/otp/send', {
        method: 'POST',
        body: JSON.stringify({ phone, email, resendTimeout: otpResendTimeout, maxOtpAttempts }),
      });
      const data = await response.json();
      if (data.type !== 'success') {
        setOtpError(data.message || 'Could not send OTP. Please try again.');
        return;
      }
      setOtpValue('');
      setOtpError('');
      setOtpVerified(false);
      setOtpSent(true);
      setOtpResendCountdown(otpResendTimeout);
      toast(data.message, { type: 'success', autoClose: 3000 });
    } catch (error) {
      setOtpError('Could not send OTP. Please try again.');
    }
  };
  const handleVerifyOtp = async (): Promise<boolean> => {
    const phone = getValuesStep2('customer.phoneNo');
    try {
      const response = await fetch('/api/otp/verify', {
        method: 'POST',
        body: JSON.stringify({ phone, otp: otpValue, maxWindowAttempts, maxTotalAttempts }),
      });
      const data = await response.json();
      if (data.verified) {
        setOtpVerified(true);
        setOtpVerifiedPhone(phone);
        setOtpError('');
        toast('Mobile number verified', { type: 'success', autoClose: 3000 });
        return true;
      }
      setOtpVerified(false);
      setOtpError(data.message || 'Invalid OTP. Please try again.');
      return false;
    } catch (error) {
      setOtpVerified(false);
      setOtpError('Could not verify OTP. Please try again.');
      return false;
    }
  };
  const wizardNext = async (gotoNext: () => void, nextStep: number) => {
    let isValid = false;
    if (currentStep === 0) {
      isValid = await triggerStep1();
      handleSubmitStep1(onSubmitStep1)();
    }
    if (currentStep === 1) {
      isValid = await triggerStep2();
      if (isValid && !otpEnabled) {
        // OTP disabled via settings: skip verification and continue.
        handleSubmitStep2(onSubmitStep2)();
      } else if (isValid) {
        const phoneVerified =
          otpVerified && otpVerifiedPhone === getValuesStep2('customer.phoneNo');
        if (phoneVerified) {
          // Already verified: proceed to the next step.
          handleSubmitStep2(onSubmitStep2)();
        } else if (!otpSent) {
          // First click acts as "Send OTP" and keeps the user on this step.
          await handleSendOtp();
          isValid = false;
        } else {
          // OTP already sent: this click verifies and, on success, advances.
          const verified = await handleVerifyOtp();
          if (verified) {
            handleSubmitStep2(onSubmitStep2)();
          } else {
            isValid = false;
          }
        }
      }
    }
    if (currentStep === 2) {
      isValid = await triggerStep3();
      try {
        await handleSubmitStep3(onSubmitStep3)();
        isValid = true;
      } catch (error) {
        setSectionLoading(false);
        isValid = false;
        toast(error instanceof Error ? error.message : String(error), {
          type: 'error',
          autoClose: 3000,
        });
      }
    }

    if (isValid) {
      gotoNext();
      setCurrentStep(nextStep);
    }
  };
  const wizardPrevious = async (gotoPrevious: () => void, previousStep: number) => {
    if (previousStep <= 2 && formData.temporaryBookingId) {
      try {
        const releaseTemporaryBooking = functions().httpsCallable('releaseTemporaryBookingV2');
        await releaseTemporaryBooking({ temporaryBookingId: formData.temporaryBookingId });
      } catch (error) {
        console.error('Failed to release temporary booking', error);
      }
      setFormData((currentData) => ({
        ...currentData,
        temporaryBookingId: undefined,
        seats: [],
      }));
    }
    gotoPrevious();
    setCurrentStep(previousStep);
  };
  const onDateChange = async (date: ButtonBarItem) => {
    // current date bookings
    const scheduleList = schedules.filter((schedule) => {
      const isAvailable = schedule.available ? schedule.available.online : false;
      return isAvailable && schedule.date === date.value;
    });
    const uniqueMovieIds = Array.from(new Set(scheduleList.map((schedule) => schedule.movie.id)));
    const currentMovieList = movies.filter((movie) => uniqueMovieIds.includes(movie.id));
    setValueStep1('movie', '');
    setValueStep1('cinema', '');
    setValueStep1('showtime', '');
    setCurrentCinema([]);
    setCurrentShowtime([]);
    // eslint-disable-next-line object-curly-spacing
    setIsValidStep((validity) => ({ ...validity, ...{ step0: false } }));
    setCurrentMovies(currentMovieList);
    setCurrentSchedules(scheduleList);
    setNoSchedulesPerDate(scheduleList.length === 0);
  };

  const onMovieChange = async (movie: ButtonBarItem) => {
    // current movie
    const currentMovieScheduleArr = currentSchedules.filter((sch) => sch.movie.id === movie.value);
    const cinemaIds = Array.from(
      new Set(currentMovieScheduleArr.map((movSch) => movSch.cinema.id)),
    );
    const currentCinemaList = cinemas.filter((cinema) => cinemaIds.includes(cinema.id));
    setValueStep1('cinema', '');
    setCurrentCinema([]);
    setCurrentShowtime([]);
    // eslint-disable-next-line object-curly-spacing
    setIsValidStep((validity) => ({ ...validity, ...{ step0: false } }));
    setCurrentCinema(currentCinemaList);
  };

  const onCinemaChange = async (cinema: ButtonBarItem) => {
    // current cinema
    const currentValues = getValuesStep1();
    const currentCinemaScheduleArr = currentSchedules.filter(
      (sch) => sch.movie.id === currentValues.movie && sch.cinema.id === cinema.value,
    );
    setValueStep1('showtime', '');
    setCurrentShowtime([]);
    // eslint-disable-next-line object-curly-spacing
    setIsValidStep((validity) => ({ ...validity, ...{ step0: false } }));
    setCurrentShowtime(currentCinemaScheduleArr);
  };

  const onShowtimeChange = async (showtime: ButtonBarItem) => {
    setScheduleId(showtime.value);
    // eslint-disable-next-line object-curly-spacing
    setIsValidStep((validity) => ({ ...validity, ...{ step0: true } }));
  };

  // Changing the movie/date/cinema from the seat step invalidates the current
  // showtime, so clear the schedule (empties the seat map) and drop any seats
  // already selected. The user must re-pick a showtime before choosing seats.
  const resetSeatSelection = () => {
    setScheduleId('');
    movieTicketRef.current?.reset();
  };
  const onDateChangeStep2 = async (date: ButtonBarItem) => {
    await onDateChange(date);
    resetSeatSelection();
  };
  const onMovieChangeStep2 = async (movie: ButtonBarItem) => {
    await onMovieChange(movie);
    resetSeatSelection();
  };
  const onCinemaChangeStep2 = async (cinema: ButtonBarItem) => {
    await onCinemaChange(cinema);
    resetSeatSelection();
  };
  const onShowtimeChangeStep2 = async (showtime: ButtonBarItem) => {
    await onShowtimeChange(showtime);
    // Keep formData (summary + scheduleObj) in sync with the new showtime.
    handleSubmitStep1(onSubmitStep1)();
    // Clear seats selected for the previous showtime; the new seat map loads
    // automatically from the updated scheduleId.
    movieTicketRef.current?.reset();
  };

  const onTermsToggle = (value: boolean) => {
    setEnablePay(value);
  };

  const onPaymentMethod = (value: string) => {
    setPaymentType(value);
  };

  const onSubscribeToggle = (value: boolean) => {
    setNewsletterSubscribed(value);
    setFormData((currentValue) => ({ ...currentValue, newsletterSubscribe: value }));
  };

  const onPopupAgree = () => {
    setIsPopupAgree(true);
  };

  const handlePaymentSubmit = () => {
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const handlePay = async () => {
    try {
      setDisablePay(true);
      setSectionLoading(true);
      setShowWarning(true);
      const timeout = settings?.temporaryBookingDelay ? settings?.temporaryBookingDelay - 120 : 600;
      // console.log('newsletterSubscribe', formData?.newsletterSubscribe);
      // console.log('email', formData?.customer?.email);
      if (formData && formData.newsletterSubscribe && formData.customer?.email) {
        const subscribe = await fetch('/api/newsletter/subscribe', {
          method: 'POST',
          body: JSON.stringify({
            email: formData.customer.email,
          }),
        });
        await subscribe.json();
        console.log('subscribed!');
      }

      if (paymentType === 'commercial') {
        const response = await fetch('/api/payment/generate-session', {
          method: 'POST',
          body: JSON.stringify({ ...formData, timeout }),
        });
        const resData = await response.json();
        if (resData.type === 'error') {
          toast(resData.message, {
            type: 'error',
            autoClose: 3000,
          });
        } else {
          // Modern (v100) Hosted Checkout: configure() takes only the session id;
          // display control is set server-side in the INITIATE_CHECKOUT request.
          Checkout.configure({
            session: {
              id: resData.sessionId,
            },
          });

          Checkout.showPaymentPage();
          setEnablePay(true);
        }
      } else if (paymentType === 'hnb') {
        // HNB Payment Gateway
        const response = await fetch('/api/payment/generate-hnb-session', {
          method: 'POST',
          body: JSON.stringify({ ...formData, timeout }),
        });
        const resData = await response.json();

        setParams((currentData) => ({
          ...currentData,
          transaction_uuid: resData.sessionData.transactionId,
        }));

        const newParams: Record<string, string> = {
          access_key: params.access_key,
          profile_id: params.profile_id,
          transaction_uuid: resData.sessionData.transactionId,
          signed_field_names: params.signed_field_names,
          unsigned_field_names: params.unsigned_field_names,
          signed_date_time: params.signed_date_time,
          locale: params.locale,
          transaction_type: params.transaction_type,
          reference_number: params.reference_number,
          amount: params.amount,
          currency: params.currency,
          bill_to_forename: params.bill_to_forename,
          bill_to_surname: params.bill_to_surname,
          bill_to_email: params.bill_to_email,
          bill_to_address_line1: params.bill_to_address_line1,
          bill_to_address_city: params.bill_to_address_city,
          bill_to_address_postal_code: params.bill_to_address_postal_code,
          bill_to_address_state: params.bill_to_address_state,
          bill_to_address_country: params.bill_to_address_country,
        };

        // Signature generation (HMAC-SHA256)
        const secretKey = process.env.NEXT_PUBLIC_CYBERSOURCE_SECRET_KEY;
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const sign = (params: Record<string, string>, secret: string): string => {
          const signedFieldNames = params.signed_field_names.split(',');
          const dataToSign = signedFieldNames.map((field) => `${field}=${params[field]}`).join(',');
          const hash = CryptoJS.HmacSHA256(dataToSign, secret);
          return CryptoJS.enc.Base64.stringify(hash);
        };

        if (params) {
          setSignature(sign(newParams, secretKey || ''));
        } else {
          console.error('Params are undefined, cannot generate signature.');
        }
        handlePaymentSubmit();
      } else if (paymentType === 'paycorp') {
        // Paycorp Payment Gateway
        try {
          const response = await fetch('/api/cybersource/paycorp', {
            method: 'POST',
            body: JSON.stringify({ ...formData, timeout }),
          });
          const resData = await response.json();
          const redirectUrl = resData.paymentPageUrl;
          window.location.href = redirectUrl;
        } catch (error) {
          console.error(error);
        }
      }
    } catch (error) {
      setEnablePay(true);
      setShowWarning(false);
      console.error(error);
      toast(
        'sorry! we are having trouble connecting with payment gateway. Please try again shortly.',
        {
          type: 'error',
          autoClose: 3000,
        },
      );
    }
  };

  const onCountDownExpires = () => {
    toast('Sorry! your time expired. Please restart the process.', {
      type: 'error',
      autoClose: 3000,
    });
    router.reload();
  };

  const localPhoneNumberValidation = (number: string) => {
    const countryCode = number.substring(0, 3);
    let returnState: boolean | string = true;
    if (countryCode === '+94') {
      if (number.length !== 12) {
        returnState = 'number should have 10 digits';
      } else {
        returnState = true;
      }
    }
    return returnState;
  };

  useEffect(() => {
    const cinemaList = cinemasStrObjs.map((cinemaStrObj) => {
      const cinemaSingle = JSON.parse(cinemaStrObj) as CinemaSingle;
      return {
        ...cinemaSingle,
        lastUpdatedAt: firebase.firestore.Timestamp.fromMillis(
          cinemaSingle.lastUpdatedAt.seconds * 1000,
        ),
      };
    });
    setCinemas(cinemaList);
    let scheduleList = schedulesStrObjs.map((scheduleStrObj) => {
      const scheduleSingle = JSON.parse(scheduleStrObj) as ScheduleSingle;
      return {
        ...scheduleSingle,
        lastUpdatedAt: firebase.firestore.Timestamp.fromMillis(
          scheduleSingle.lastUpdatedAt.seconds * 1000,
        ),
      };
    });
    // Show Today's Schedule or Tomorrows Schedules
    // last schedule time
    const day = getDayOfWeek(startOfDay(new Date()));
    const daySchedules = cinemaList
      .map((cinema) => {
        return cinema.showtimes.find((showtime) => {
          return showtime.id === day;
        });
      })
      .map((schedule) => schedule?.times);
    const maxShowtimeInMinutes = reduce(
      maxBy((item: number) => item),
      0,
      flatten(daySchedules).map((schedule) =>
        schedule
          ? getHours(parse(schedule.time, timeFormat, new Date())) * 60 +
            getMinutes(parse(schedule.time, timeFormat, new Date()))
          : 0,
      ),
    );
    // update current days based on available schedules
    const lastSchedule = setMinutes(startOfDay(new Date()), maxShowtimeInMinutes);
    const rightNow = new Date();
    let showToday = true;
    const daysList: string[] = [];
    const currentDays: Day[] = [];
    // for (let i = 0; i < 29; i += 1) {
    //   const date = addDays(startOfDay(new Date()), i);
    //   currentDays.push({
    //     value: format(date, 'dd-MM-yyyy'),
    //     label: i === 0 ? `Today` : `${format(date, 'ccc, d MMM')}`,
    //   });
    // }
    scheduleList.forEach((schedule) => {
      if (!daysList.includes(schedule.date)) {
        daysList.push(schedule.date);
      }
    });

    daysList.forEach((currentDay) => {
      const date = parse(currentDay, 'dd-MM-yyyy', new Date());
      const today = isToday(date);
      currentDays.push({
        value: format(date, 'dd-MM-yyyy'),
        label: today ? `Today` : `${format(date, 'ccc, d MMM')}`,
      });
    });

    next28Days.current = currentDays;

    const movieList = moviesStrObjs.map((movieStrObj) => {
      const movieSingle = JSON.parse(movieStrObj) as MovieSingle;
      return {
        ...movieSingle,
        releaseDate: firebase.firestore.Timestamp.fromMillis(
          movieSingle.releaseDate.seconds * 1000,
        ),
        lastUpdatedAt: firebase.firestore.Timestamp.fromMillis(
          movieSingle.lastUpdatedAt.seconds * 1000,
        ),
        premiereDate: movieSingle.premiereDate
          ? firebase.firestore.Timestamp.fromMillis(movieSingle.premiereDate.seconds * 1000)
          : undefined,
      };
    });
    setMovies(movieList);

    if (
      differenceInMinutes(lastSchedule, rightNow) <
      bookingSettings.current.onlineBookingBlockPriorInMins
    ) {
      showToday = false;
    }

    // Only remove the first date entry when today is actually the first entry.
    // Checking the 'Today' label (set only when isToday(date) is true) avoids
    // deleting tomorrow's date when today has no available schedules.
    if (!showToday && next28Days.current[0]?.label === 'Today') {
      next28Days.current.shift();
    }
    // we don't need it as we are getting only for days where schedules are available
    // else if (schedulesStrObjs.length > 2) {
    // next28Days.current.pop();
    // }

    // console.log(scheduleList.map((item) => item.dateforSort));

    // remove todays all past schedules
    if (showToday) {
      // remove all past schedules (with offset) for today
      scheduleList = scheduleList.filter((schedule) => {
        const sameDay = isSameDay(
          startOfDay(parse(schedule.date, dateFormat, new Date())),
          startOfDay(new Date()),
        );

        let include = true;

        if (sameDay) {
          const showtimeDateObj = parse(schedule.showtime.time, timeFormat, new Date());
          const minutes = getHours(showtimeDateObj) * 60 + getMinutes(showtimeDateObj);
          const todayShowtimeDate = setMinutes(startOfDay(new Date()), minutes);
          include =
            differenceInMinutes(todayShowtimeDate, new Date()) >=
            bookingSettings.current.onlineBookingBlockPriorInMins;
        }

        return include;
      });
    } else {
      // remove all the schedules for today
      scheduleList = scheduleList.filter(
        (schedule) =>
          !isSameDay(
            startOfDay(parse(schedule.date, dateFormat, new Date())),
            startOfDay(new Date()),
          ),
      );
    }
    setSchedules(scheduleList);
    setNoSchedules(scheduleList.length === 0);

    if (cinemaList.length > 0 && scheduleList.length > 0 && movieList.length > 0) {
      dataReady.current = true;
    }
  }, [moviesStrObjs, schedulesStrObjs, cinemasStrObjs]);

  useEffect(() => {
    const checkOnParams = () => {
      // check if movie is already selected
      if (query.movie && movies.length > 0 && cinemas.length > 0 && schedules.length > 0) {
        const selectedMovie = movies.find((movie) => movie.slug === (query.movie as string));
        // find the first schedule
        if (selectedMovie) {
          const selectedSchedule = schedules.find(
            (schedule) => schedule.movie.id === selectedMovie?.id,
          );
          if (selectedSchedule) {
            setValueStep1('date', selectedSchedule.date);
            const scheduleList = schedules.filter(
              (schedule) => schedule.date === selectedSchedule.date,
            );
            setNoSchedulesPerDate(scheduleList.length === 0);
            const uniqueMovieIds = Array.from(
              new Set(scheduleList.map((schedule) => schedule.movie.id)),
            );
            const currentMovieList = movies.filter((movie) => uniqueMovieIds.includes(movie.id));
            setCurrentMovies(currentMovieList);
            setCurrentSchedules(scheduleList);
            setValueStep1('movie', selectedMovie.id);
            const currentMovieScheduleArr = scheduleList.filter(
              (sch) => sch.movie.id === selectedMovie.id,
            );
            const cinemaIds = Array.from(
              new Set(currentMovieScheduleArr.map((movSch) => movSch.cinema.id)),
            );

            const currentCinemaList = cinemas.filter((cinema) => cinemaIds.includes(cinema.id));
            setValueStep1('date', selectedSchedule.date);
            setValueStep1('movie', selectedMovie.id);
            setValueStep1('cinema', '');
            setCurrentCinema(currentCinemaList);
          } else {
            setNoSchedulesPerDate(true);
          }
        } else {
          setNoSchedulesPerDate(true);
        }
      }
    };
    if (firstTime.current && movies.length > 0 && cinemas.length > 0) {
      if (schedules.length > 0) {
        if (query.movie) {
          checkOnParams();
        }
        setLoading(false);
        firstTime.current = false;
      } else if (noSchedulesPerDate === true) {
        setLoading(false);
        firstTime.current = false;
      }
    }

    firebase
      .firestore()
      .collection('settings')
      .doc('fees')
      .get()
      .then((feeSnapshot) => {
        const feeData = feeSnapshot.data() as { onlineFee: number };
        const currentOnlineFee = feeData.onlineFee || 0;
        setOnlineFee(currentOnlineFee);
      });
  }, [
    cinemas,
    currentSchedules,
    movies,
    noSchedulesPerDate,
    query.movie,
    schedules,
    setValueStep1,
  ]);

  useEffect(() => {
    setTimeout(() => {
      clearErrorsStep1();
    }, 1);
  }, [currentStep, clearErrorsStep1]);

  useEffect(() => {
    // Register the global callbacks referenced by the checkout.min.js script tag
    // (data-error / data-cancel) for modern (v100) Hosted Checkout.
    const win = (window as unknown) as {
      errorCallback?: (error: unknown) => void;
      cancelCallback?: () => void;
    };
    win.errorCallback = (error: unknown) => {
      console.error('MPGS checkout error', error);
      toast('Sorry! something went wrong with the payment. Please try again.', {
        type: 'error',
        autoClose: 3000,
      });
      setEnablePay(true);
      setDisablePay(false);
      setShowWarning(false);
      setSectionLoading(false);
    };
    win.cancelCallback = () => {
      setEnablePay(true);
      setDisablePay(false);
      setShowWarning(false);
      setSectionLoading(false);
    };
  }, []);

  useEffect(() => {
    if (otpResendCountdown <= 0) {
      return undefined;
    }
    const timer = setInterval(() => {
      setOtpResendCountdown((seconds) => (seconds > 0 ? seconds - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpResendCountdown]);

  useEffect(() => {
    const removeTempTransaction = async (transId: string) => {
      const response = await fetch('/api/transaction/delete', {
        method: 'POST',
        body: JSON.stringify({ id: transId }),
      });
      await response.json();
    };

    if (routerQuery.timeout) {
      removeTempTransaction(routerQuery.timeout as string);
      toast(
        'Sorry! You have exceeded the time allowed to make the payment. Please make the reservation again.',
        {
          type: 'error',
          autoClose: false,
        },
      );

      push('/buy-tickets', undefined, { shallow: true });
    }
  }, [routerQuery.timeout, push]);

  const idleTimerReached = () => {
    // eslint-disable-next-line object-curly-spacing
    reload();
  };

  useEffect(() => {
    if (headerRef.current) {
      headerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }, [currentStep]);

  useEffect(() => {
    if (headerRef.current && showWarning) {
      headerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }, [showWarning]);

  const step1PhoneVerified = otpVerified && otpVerifiedPhone === getValuesStep2('customer.phoneNo');
  let nextButtonLabel = 'Next';
  if (otpEnabled && currentStep === 1 && !step1PhoneVerified) {
    nextButtonLabel = otpSent ? 'Verify and Next' : 'Send OTP';
  }

  return (
    <Layout>
      <Head>
        <script
          async
          src="https://cbcmpgs.gateway.mastercard.com/static/checkout/checkout.min.js"
          data-error="errorCallback"
          data-cancel="cancelCallback"
        />
      </Head>
      {loadRecaptchaScript()}
      <SEO title="Buy Tickets" />
      {currentStep === 3 ? (
        <TermsPopup onAgree={() => onPopupAgree()} onDisagree={() => console.log('disagreed')} />
      ) : null}
      <BuyTicketsStyled>
        <div className="content-container">
          <h2
            className="text-white text-3xl font-bold mb-6 my-6 mx-4 lg:mx-8 mt-32"
            style={{ fontSize: 32 }}>
            Buy Tickets
          </h2>
          {bookingSettings?.current.temporaryDisable === true || noSchedules === true ? (
            <StepStyled>
              <div className="text-white w-1/2 mx-auto text-center p-3 bg-yellow-600 my-10">
                Sorry!{' '}
                {noSchedules
                  ? 'no showtimes are available at the moment. Please come back again.'
                  : 'Booking is temporary disabled at the moment.'}
              </div>
            </StepStyled>
          ) : (
            <Wizard
              header={<WizardHeader ref={headerRef} onCountDownExpires={onCountDownExpires} />}
              footer={
                <WizardFooter
                  loading={sectionLoading}
                  disableNext={(step) => !isValidStep[`step${step}`]}
                  nextLabel={nextButtonLabel}
                  handlePrevious={wizardPrevious}
                  handleNext={wizardNext}
                  enablePay={enablePay}
                  disablePay={disablePay}
                  handlePay={handlePay}
                />
              }>
              <StepStyled>
                <>
                  {loading ? (
                    <Loading />
                  ) : (
                    <form>
                      <Timeout duration={600} onComplete={idleTimerReached} />
                      <div className="form-block mb-5">
                        <h3 className="text-blue-100 text-2xl font-bold">Select a date</h3>
                        <ButtonBar
                          register={registerStep1('date', { required: true })}
                          items={next28Days.current}
                          validation={errorsStep1.date}
                          onChange={onDateChange}
                        />
                      </div>
                      <div className="form-block mb-5">
                        {noSchedulesPerDate ? (
                          <h4 className="flex text-red-500 my-10 w-full text-center">
                            <span className="text-lg mx-auto">
                              No schedules available for selected {query.movie ? 'movie' : 'date'}
                            </span>
                          </h4>
                        ) : (
                          <>
                            {currentMovies.length > 0 && (
                              <>
                                <h3 className="text-blue-100 text-2xl font-bold">Select a movie</h3>
                                <ButtonBar
                                  register={registerStep1('movie', { required: true })}
                                  items={currentMovies.map((movie) => ({
                                    label: movie.name,
                                    value: movie.id,
                                  }))}
                                  onChange={onMovieChange}
                                  validation={errorsStep1.movie}
                                />
                              </>
                            )}
                          </>
                        )}
                      </div>
                      <div className="form-block mb-5">
                        {currentCinema.length > 0 && (
                          <>
                            <h3 className="text-blue-100 text-2xl font-bold">Select a cinema</h3>
                            <ButtonBar
                              register={registerStep1('cinema', { required: true })}
                              items={currentCinema.map((cinema) => ({
                                label: cinema.label,
                                value: cinema.id,
                              }))}
                              onChange={onCinemaChange}
                              validation={errorsStep1.cinema}
                            />
                          </>
                        )}
                      </div>
                      <div className="form-block mb-5">
                        {currentShowtime.length > 0 && (
                          <>
                            <h3 className="text-blue-100 text-2xl font-bold">Select a showtime</h3>
                            <ButtonBar
                              register={registerStep1('showtime', { required: true })}
                              items={currentShowtime.map((schedule) => ({
                                label: schedule.showtime.time,
                                value: schedule.id,
                              }))}
                              onChange={onShowtimeChange}
                              validation={errorsStep1.showtime}
                            />
                          </>
                        )}
                      </div>
                    </form>
                  )}
                </>
              </StepStyled>
              <StepStyled>
                {currentStep === 1 ? (
                  <form>
                    <Timeout duration={600} onComplete={idleTimerReached} />
                    <ContentBlockStyled>
                      <div className="content-container px2 lg:px-8 pt-10">
                        <div className="form-block mb-5">
                          <div className="flex flex-row">
                            <span className="text-blue-100 text-1xl font-bold block w-4/12 self-center align-middle pr-2">
                              Your Name
                            </span>
                            <input
                              className={`form-input mt-1 block border border-gray-200 bg-black rounded-md text-white w-8/12 ${
                                errorsStep2.customer?.name ? 'border-red-400 rounded-b-none' : null
                              }`}
                              placeholder="Please enter your name here"
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              {...registerStep2('customer.name', {
                                required: 'Please enter your name',
                              })}
                            />
                          </div>
                          {errorsStep2.customer?.name ? (
                            <div className="flex flex-col w-full items-end">
                              <span className="flex bg-red-500 text-white w-8/12 px-3 py-1 text-xs rounded-b-md self-end">
                                {errorsStep2.customer?.name.message}
                              </span>
                            </div>
                          ) : null}
                        </div>
                        <div className="form-block mb-5">
                          <label className="flex flex-row" htmlFor="customer-email">
                            <span className="text-blue-100 text-1xl font-bold block w-4/12 self-center align-middle pr-2">
                              Your Email
                            </span>
                            <input
                              id="customer-email"
                              className={`form-input mt-1 block w-8/12 border border-gray-200 bg-black rounded-md text-white ${
                                errorsStep2.customer?.email ? 'border-red-400 rounded-b-none' : null
                              }`}
                              placeholder="Please enter a valid email address"
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              {...registerStep2('customer.email', {
                                required: 'Please enter your email address',
                                pattern: {
                                  value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                  message: 'Please enter a valid email address',
                                },
                              })}
                            />
                          </label>
                          {errorsStep2.customer?.email ? (
                            <div className="flex flex-col w-full items-end">
                              <span className="flex bg-red-500 text-white w-8/12 px-3 py-1 text-xs rounded-b-md self-end">
                                {errorsStep2.customer?.email.message}
                              </span>
                            </div>
                          ) : null}
                        </div>
                        <div className="form-block mb-5">
                          <label htmlFor="customer-phone" className="flex flex-row">
                            <span className="text-blue-100 text-1xl font-bold block w-4/12 self-center align-middle pr-2">
                              Your Mobile No
                            </span>
                            <input
                              id="customer-phone"
                              className={`form-input mt-1 block w-8/12 border border-gray-200 bg-black rounded-md text-white ${
                                errorsStep2.customer?.phoneNo
                                  ? 'border-red-400 rounded-b-none'
                                  : null
                              }`}
                              placeholder="Please enter your mobile number"
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              {...registerStep2('customer.phoneNo', {
                                required: 'Please enter your mobile number',
                                pattern: {
                                  value: /^\+[1-9]\d{3,14}$/,
                                  message:
                                    'Please enter a valid mobile number with country eg: (+9477xxxxxxx)',
                                },
                                validate: localPhoneNumberValidation,
                              })}
                            />
                          </label>
                          {errorsStep2.customer?.phoneNo ? (
                            <div className="flex flex-col w-full items-end">
                              <span className="flex bg-red-500 text-white w-8/12 px-3 py-1 text-xs rounded-b-md self-end">
                                {errorsStep2.customer?.phoneNo.message}
                              </span>
                            </div>
                          ) : null}
                        </div>
                        {otpEnabled ? (
                          <div className="form-block mb-5">
                            <div className="flex flex-row">
                              <span className="text-blue-100 text-1xl font-bold block w-4/12 self-center align-middle pr-2">
                                Verify Mobile
                              </span>
                              {otpVerified &&
                              otpVerifiedPhone === getValuesStep2('customer.phoneNo') ? (
                                <span className="flex bg-green-600 text-white w-8/12 px-3 py-2 text-sm rounded-md self-center">
                                  Your mobile number has been verified.
                                </span>
                              ) : (
                                <div className="w-8/12 flex flex-col">
                                  {otpSent ? (
                                    <>
                                      <div className="mt-1">
                                        <OtpInput
                                          value={otpValue}
                                          onChange={(value) => {
                                            setOtpValue(value);
                                            setOtpError('');
                                          }}
                                          length={6}
                                          hasError={!!otpError}
                                        />
                                      </div>
                                      {otpError ? (
                                        // <span className="mt-2 bg-red-500 text-white px-3 py-1 text-xs rounded-md self-stretch">
                                        //   {otpError}
                                        // </span>
                                        <div className="flex flex-col w-full">
                                          <span className="block bg-red-500 text-white w-full px-3 py-1 text-xs rounded-b-md text-center">
                                            {otpError}
                                          </span>
                                        </div>
                                      ) : null}
                                      <button
                                        type="button"
                                        disabled={otpResendCountdown > 0}
                                        className={`mt-2 px-4 py-2 rounded-md text-sm self-start ${
                                          otpResendCountdown > 0
                                            ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                                            : 'bg-blue-600 text-white'
                                        }`}
                                        onClick={handleSendOtp}>
                                        {otpResendCountdown > 0
                                          ? `Resend OTP in ${Math.floor(
                                              otpResendCountdown / 60,
                                            )}:${String(otpResendCountdown % 60).padStart(2, '0')}`
                                          : 'Resend OTP'}
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-gray-300 text-sm self-center">
                                        Click &ldquo;Send OTP&rdquo; below to receive a verification
                                        code on your mobile.
                                      </span>
                                      {otpError ? (
                                        <div className="flex flex-col w-full">
                                          <span className="block bg-red-500 text-white w-full px-3 py-1 text-xs rounded-b-md text-center">
                                            {otpError}
                                          </span>
                                        </div>
                                      ) : null}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </ContentBlockStyled>
                  </form>
                ) : null}
              </StepStyled>
              <StepStyled>
                {currentStep === 2 ? (
                  <div className="form-block mb-5">
                    <Timeout duration={600} onComplete={idleTimerReached} />
                    <h3 className="text-blue-100 text-2xl font-bold">Pick a Seat</h3>
                    <div className="my-4">
                      <div className="flex flex-row items-center mb-1">
                        <span className="text-blue-100 text-sm font-bold w-24 flex-shrink-0">
                          Date
                        </span>
                        <div className="flex-1">
                          <ButtonBar
                            register={registerStep1('date', { required: true })}
                            items={next28Days.current}
                            validation={errorsStep1.date}
                            onChange={onDateChangeStep2}
                            compact
                          />
                        </div>
                      </div>
                      {currentMovies.length > 0 ? (
                        <div className="flex flex-row items-center mb-1">
                          <span className="text-blue-100 text-sm font-bold w-24 flex-shrink-0">
                            Movie
                          </span>
                          <div className="flex-1">
                            <ButtonBar
                              register={registerStep1('movie', { required: true })}
                              items={currentMovies.map((movie) => ({
                                label: movie.name,
                                value: movie.id,
                              }))}
                              onChange={onMovieChangeStep2}
                              validation={errorsStep1.movie}
                              compact
                            />
                          </div>
                        </div>
                      ) : null}
                      {currentCinema.length > 0 ? (
                        <div className="flex flex-row items-center mb-1">
                          <span className="text-blue-100 text-sm font-bold w-24 flex-shrink-0">
                            Cinema
                          </span>
                          <div className="flex-1">
                            <ButtonBar
                              register={registerStep1('cinema', { required: true })}
                              items={currentCinema.map((cinema) => ({
                                label: cinema.label,
                                value: cinema.id,
                              }))}
                              onChange={onCinemaChangeStep2}
                              validation={errorsStep1.cinema}
                              compact
                            />
                          </div>
                        </div>
                      ) : null}
                      {currentShowtime.length > 0 ? (
                        <div className="flex flex-row items-center">
                          <span className="text-blue-100 text-sm font-bold w-24 flex-shrink-0">
                            Showtime
                          </span>
                          <div className="flex-1">
                            <ButtonBar
                              register={registerStep1('showtime', { required: true })}
                              items={currentShowtime.map((schedule) => ({
                                label: schedule.showtime.time,
                                value: schedule.id,
                              }))}
                              onChange={onShowtimeChangeStep2}
                              validation={errorsStep1.showtime}
                              compact
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                    {scheduleId ? (
                      <MovieTickets
                        ref={movieTicketRef}
                        useFormReturn={restUseFormReturn}
                        scheduleId={scheduleId}
                        cinema={cinemas.find((cinema) => cinema.id === getValuesStep1('cinema'))}
                        loading={sectionLoading}
                        onlineFee={onlineFee}
                      />
                    ) : (
                      <div className="text-yellow-300 text-center my-10">
                        Please select a showtime above to choose your seats.
                      </div>
                    )}
                  </div>
                ) : null}
              </StepStyled>
              <StepStyled>
                {currentStep === 3 ? (
                  <div className="form-block mb-5">
                    <form
                      ref={formRef}
                      method="post"
                      action={process.env.NEXT_PUBLIC_CYBERSOURCE_FORM_URL}>
                      {Object.entries(params).map(([key, value]) => (
                        <input key={key} type="hidden" name={key} value={value} />
                      ))}
                      <input type="hidden" name="signature" value={signature} />
                    </form>
                    {sectionLoading ? (
                      <>
                        <div className="text-center px-5 mt-10 text-yellow-300">
                          <h3>Payment processing</h3>
                          Please do not refresh or close your browser, you will be redirected to the
                          payment gateway momentarily.
                        </div>
                        <Loading />
                      </>
                    ) : (
                      <>
                        <h3 className="text-blue-100 text-2xl text-center font-bold mt-10">
                          Summary
                        </h3>
                        {formData && (
                          <WizardSummary
                            onTermsToggle={onTermsToggle}
                            onSubscribeToggle={onSubscribeToggle}
                            onPaymentMethod={onPaymentMethod}
                            isPopupAgree={isPopupAgree}
                            newsletterSubscribed={newsletterSubscribed}
                            onlineFee={onlineFee}
                            data={formData}
                          />
                        )}
                      </>
                    )}
                  </div>
                ) : null}
              </StepStyled>
            </Wizard>
          )}
        </div>
      </BuyTicketsStyled>
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps<BuyTicketsProps> = async () => {
  const bookingSettingsSnap = await firebase
    .firestore()
    .collection('settings')
    .doc('bookings')
    .get();
  const bookingSettings = bookingSettingsSnap.data() as SettingsBookingType;

  const moviesQuerySnap = await firebase
    .firestore()
    .collection('movies')
    .where('status', '==', MovieStatus.active)
    .get();

  const moviesStrObjs: string[] = [];
  moviesQuerySnap.forEach((movie) => {
    const movieObj: Movie = { ...(movie.data() as Movie), id: movie.id };
    const movieStrObj = JSON.stringify(movieObj);
    moviesStrObjs.push(movieStrObj);
  });

  // schedules for next 28 days
  const startDate = format(startOfDay(new Date()), 'yyyyMMdd');
  const endDate = format(addDays(startOfDay(new Date()), 42), 'yyyyMMdd');

  const scheduleQuerySnap = await firebase
    .firestore()
    .collection('schedules')
    .where('dateforSort', '>=', startDate)
    .where('dateforSort', '<=', endDate)
    .get();
  const currentSchedules: string[] = [];
  scheduleQuerySnap.forEach((schedule) => {
    const scheduleItem: Schedule = { ...(schedule.data() as Schedule), id: schedule.id };
    const isAvailable = scheduleItem.available ? scheduleItem.available.online : false;
    if (isAvailable) {
      const scheduleItemObj = JSON.stringify(scheduleItem);
      currentSchedules.push(scheduleItemObj);
    }
  });
  const cinemasQuerySnap = await firebase.firestore().collection('cinemas').get();

  const cinemasStrObjs: string[] = [];
  const cinemasList: Cinema[] = [];
  cinemasQuerySnap.forEach((cinema) => {
    const cinemaObj = { ...(cinema.data() as Cinema), id: cinema.id };
    cinemasList.push(cinemaObj);
    const cinemaStrObj = JSON.stringify(cinemaObj);
    cinemasStrObjs.push(cinemaStrObj);
  });

  // Show Today's Schedule or Tomorrows Schedules
  // last schedule time
  const day = getDayOfWeek(startOfDay(new Date()));
  const daySchedules = cinemasList
    .map((cinema) => {
      return cinema.showtimes.find((showtime) => {
        return showtime.id === day;
      });
    })
    .map((schedule) => schedule?.times);

  const maxShowtimeInMinutes = reduce(
    maxBy((item: number) => item),
    0,
    flatten(daySchedules).map((schedule) =>
      schedule
        ? getHours(parse(schedule.time, timeFormat, new Date())) * 60 +
          getMinutes(parse(schedule.time, timeFormat, new Date()))
        : 0,
    ),
  );

  const lastSchedule = setMinutes(startOfDay(new Date()), maxShowtimeInMinutes);
  const rightNow = new Date();
  let showToday = true;
  if (differenceInMinutes(lastSchedule, rightNow) < bookingSettings.onlineBookingBlockPriorInMins) {
    showToday = false;
  }

  if (!showToday) {
    // Client-side isSameDay filtering already removes all of today's schedules.
    // The old currentSchedules.shift() removed only the first schedule, which
    // deleted a tomorrow schedule whenever today had no available schedules.
  } else {
    // currentSchedules.pop();
  }

  const returnMovies: GetStaticPropsResult<BuyTicketsProps> = {
    props: {
      moviesStrObjs,
      cinemasStrObjs,
      schedulesStrObjs: currentSchedules,
      bookingSettingsStr: JSON.stringify(bookingSettings),
    },
  };

  return returnMovies;
};
